import { Anchor, Group, Image } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useMemo } from 'react';

import { WwaaveLogo } from './WwaaveLogo';

/**
 * Read the custom logo key from the branding data blob injected by the Worker's
 * HTMLRewriter. Returns the R2-backed `/img/<key>` path when an admin has
 * uploaded a custom logo, otherwise null (use the built-in inline brand mark).
 */
function useCustomLogoSrc(): string | null {
  return useMemo(() => {
    if (typeof document === 'undefined') return null;
    try {
      const el = document.getElementById('__wwaave_branding_data__');
      if (el?.textContent) {
        const data = JSON.parse(el.textContent) as { logoKey?: string };
        if (data.logoKey) return `/${data.logoKey}`;
      }
    } catch {
      // Malformed JSON or missing element — fall back to the built-in mark.
    }
    return null;
  }, []);
}

/**
 * BrandLogoLink — the WWAAVE brand mark wrapped in a typed Link to home (or
 * /admin for AdminShell). Single source of truth across AppHeader, PublicHeader,
 * HomeHeader, AuthShell and AdminShell.
 *
 * Default: the inline `WwaaveLogo` (ripple mark + Darker Grotesque wordmark),
 * which renders in-font and recolours for dark mode via the `ink` token. When an
 * admin has uploaded a custom logo via Admin > Branding, that R2-hosted image is
 * shown instead (alt "WWAAVE").
 *
 * Pass `trailing` to render an inline label next to the logo (used by AdminShell
 * to pair the wordmark with an "Admin" caption).
 */
export function BrandLogoLink({
  height = 32,
  to = '/',
  trailing,
  ariaLabel = 'WWAAVE home',
}: {
  height?: number;
  to?: '/' | '/admin';
  trailing?: ReactNode;
  ariaLabel?: string;
}) {
  const customSrc = useCustomLogoSrc();

  const mark = customSrc ? (
    <Image src={customSrc} alt="WWAAVE" h={height} w="auto" fit="contain" />
  ) : (
    <WwaaveLogo height={height} />
  );

  if (trailing) {
    return (
      <Anchor
        component={Link}
        to={to}
        underline="never"
        aria-label={ariaLabel}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <Group gap="xs" wrap="nowrap" align="center">
          {mark}
          {trailing}
        </Group>
      </Anchor>
    );
  }

  return (
    <Anchor component={Link} to={to} underline="never" aria-label={ariaLabel}>
      {mark}
    </Anchor>
  );
}
